import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LEGAL_DOCS } from '../data/legalDocs';

const ACCENT = '#137274';

/**
 * Modal for Terms of Service / Privacy Policy.
 * @param {'terms' | 'privacy' | null} docKey
 */
const LegalModal = ({ docKey, open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const doc = docKey ? LEGAL_DOCS[docKey] : null;

  if (!doc) return null;

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      // Sit above AuthModal (z-index ~1300)
      sx={{ zIndex: 1500 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: isDark ? '#121a1c' : '#fff',
          backgroundImage: isDark
            ? `radial-gradient(ellipse 80% 40% at 50% 0%, ${alpha(ACCENT, 0.18)}, transparent 55%)`
            : `radial-gradient(ellipse 80% 40% at 50% 0%, ${alpha(ACCENT, 0.08)}, transparent 55%)`,
          maxHeight: '85vh',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: alpha('#020808', 0.55),
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          pr: 6,
          pb: 1,
          fontWeight: 900,
          fontSize: '1.2rem',
          letterSpacing: '-0.03em',
        }}
      >
        {doc.title}
        <Typography
          component="div"
          sx={{ mt: 0.5, fontSize: '0.75rem', fontWeight: 600, color: muted }}
        >
          Last updated {doc.updated}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12, color: muted }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: border, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {doc.sections.map((section) => (
            <Box key={section.heading}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '0.9rem',
                  mb: 0.6,
                  letterSpacing: '-0.01em',
                }}
              >
                {section.heading}
              </Typography>
              <Typography
                sx={{
                  color: muted,
                  fontSize: '0.84rem',
                  lineHeight: 1.65,
                }}
              >
                {section.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.75 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            bgcolor: ACCENT,
            borderRadius: 2,
            px: 2.5,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
          }}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LegalModal;
